import {
    DefaultButton,
    Dialog,
    DialogFooter,
    MessageBar,
    MessageBarType,
    PrimaryButton, ProgressIndicator,
    Stack,
    TextField
} from '@fluentui/react';
import React, {useEffect} from 'react';
import {ICopyStorefrontRequest, IGlobalStorefrontSettings, IStorefrontSettings, IStorefrontTemplate} from '../../models/Storefront';
import {
    useCopyStorefrontMutation,
    useCreateStorefrontMutation
} from '../../store/endpoints/StorefrontSettingsEndpoints';
import {useCreateStorefrontFromTemplateMutation} from '../../store/endpoints/StorefrontTemplateEndpoints';
import {cloneDeep} from "lodash";
import {useGetStorefrontQuestionsQuery} from "../../store/endpoints/StorefrontQuestionEndpoints";
import StorefrontDomainDropdown from './StorefrontDomainDropdown';

interface INewStorefrontPanelProps {
    isOpen: boolean;
    onAddStorefront: () => void;
    onClose: () => void;
    globalSettings?: IGlobalStorefrontSettings;
    // When present, the new storefront is provisioned from this template instead of
    // the client-merged global defaults.
    template?: IStorefrontTemplate;
    // Starting style presets picked in the template picker (ids of the template's own styles).
    // Undefined = the API keeps whatever the template captured as active.
    templatePresets?: {paletteId?: string; fontPackId?: string};
}

export const NEW_STOREFRONT: IStorefrontSettings = {
        name: '',
        logo: '',
        title: '',
        bannerText: '',
        menuBarColor: '',
        menuBarTextColor: '',
        backgroundColor: '',
        textColor: '',
        headerFont: '',
        defaultFont: '',
        termsAndConditions: '',
        orderEmail: '',
        customerEmailText: '',
        customerEmailSubject: '',
        checkoutEnabled: false,
        checkoutType: '',
        defaultMarkup: 0,
        defaultMarkupType: '',
        subUrl: '',
        enabled: true,
        fullCatalogs: [],
        overrideTermsAndConditions: false, 
        useCategories: true
    }

const NewStorefrontDialog: React.FC<INewStorefrontPanelProps> = (props) => {
    const [createStorefront] = useCreateStorefrontMutation();
    const [createStorefrontFromTemplate] = useCreateStorefrontFromTemplateMutation();
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>()
    const [saving, setSaving] = React.useState(false);
    const [externalDataLoading, setExternalDataLoading] = React.useState(false);
    const [newStorefront, setNewStorefront] = React.useState<IStorefrontSettings>(NEW_STOREFRONT);
    
    const globalQuestions = useGetStorefrontQuestionsQuery('global', {skip: !props.isOpen})

    useEffect(() => {
        if(props.globalSettings){
            const globalDefaultedNewStorefront = cloneDeep(NEW_STOREFRONT);
            globalDefaultedNewStorefront.useHeaderSearch = props.globalSettings.useHeaderSearch;
            globalDefaultedNewStorefront.includeFooter = props.globalSettings.includeFooter;
            globalDefaultedNewStorefront.useCustomEmailTemplate = props.globalSettings.useCustomEmailTemplate;
            globalDefaultedNewStorefront.bannerHeight = props.globalSettings.bannerHeight;
            globalDefaultedNewStorefront.customEmailTemplate = props.globalSettings.customEmailTemplate;
            globalDefaultedNewStorefront.useCategories = props.globalSettings.useCategories;
            
            setNewStorefront(globalDefaultedNewStorefront);
            if(globalQuestions.data?.items){
                globalDefaultedNewStorefront.checkoutQuestionIds = globalQuestions.data.items?.filter(a => a.default && a.id)?.map((q) => q.id ?? '') ?? [];
            }
        }

    }, [props.globalSettings, globalQuestions]);
    
    const save = () => {
        setSaving(true);
        setErrorMessage('')
        const request = props.template?.id
            ? createStorefrontFromTemplate({
                templateId: props.template.id,
                name: newStorefront.name ?? '',
                subUrl: newStorefront.subUrl,
                domain: newStorefront.domain,
                paletteId: props.templatePresets?.paletteId,
                fontPackId: props.templatePresets?.fontPackId,
            }).unwrap()
            : createStorefront(newStorefront).unwrap();
        request.then((r) => {
            setSaving(false);
            if(r.success) {
                props.onAddStorefront();
            }
            else{
                setErrorMessage(r.message ?? 'an error occurred while creating new storefront')
            }
        });
    }

    const validated = () => {
        if (!newStorefront.name || newStorefront.name.trim() === '') {
            return false;
        }
        if (!newStorefront.subUrl || newStorefront.subUrl.trim() === '') {
            return false;
        }
        return true;
    }

    return (
        <Dialog
            hidden={!props.isOpen}
            onDismiss={props.onClose}
            dialogContentProps={{
                title: 'New Storefront',
                showCloseButton: true,
                subText: props.template ? `Using template: ${props.template.name}` : undefined,
            }}
        >
            <Stack tokens={{childrenGap: '1em'}}>
                {errorMessage && <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setErrorMessage(undefined)}>{errorMessage}</MessageBar>}
                <TextField label='Storefront Sub-URL' value={newStorefront.subUrl} onChange={(_, val) => setNewStorefront({...newStorefront, subUrl: val ?? ''})} required />
                <StorefrontDomainDropdown value={newStorefront.domain} onChange={(val) => setNewStorefront({...newStorefront, domain: val})} label='Domain' />
                <TextField label="Storefront Name" value={newStorefront.name} onChange={(_, val) => setNewStorefront({...newStorefront, name: val ?? ''})} required />
            </Stack>
            <DialogFooter>
                <Stack horizontal tokens={{childrenGap: '1em'}}>
                    <PrimaryButton disabled={saving || externalDataLoading || !validated()} onClick={save}>Add Storefront</PrimaryButton>
                    <DefaultButton disabled={saving} onClick={() => {props.onClose(); setErrorMessage('')}}>Cancel</DefaultButton>
                </Stack>
                {errorMessage && <MessageBar messageBarType={MessageBarType.error}>{errorMessage}</MessageBar>}
            </DialogFooter>
        </Dialog>
    )
}

interface ICopyStorefrontPanelProps {
    isOpen: boolean;
    onAddStorefront: () => void;
    onClose: () => void;
    settings?: IStorefrontSettings;
}

const CopyStorefrontDialog: React.FC<ICopyStorefrontPanelProps> = (props) => {
    const [copyStorefront] = useCopyStorefrontMutation();
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>()
    const [saving, setSaving] = React.useState(false);
    const [externalDataLoading, setExternalDataLoading] = React.useState(false);
    const [newStorefront, setNewStorefront] = React.useState<ICopyStorefrontRequest>({name: '',subUrl: '', sourceStorefrontId: ''});

    useEffect(() => {
        if(props.settings?.id && newStorefront) {
            setNewStorefront({name: `${props.settings.name}-Copy`, sourceStorefrontId: props.settings.id, subUrl: `${props.settings.subUrl}_1`});
        }
        
    }, [props.settings]);
    

    const save = () => {
        setSaving(true);
        copyStorefront(newStorefront).unwrap().then((r) => {
            setSaving(false);
            if(r.success) {
                props.onAddStorefront();
            }
            else{
                setErrorMessage(r.message ?? 'an error occurred while creating new storefront')
            }
        });
    }

    const validated = () => {
        if (!newStorefront.name || newStorefront.name.trim() === '') {
            return false;
        }
        if (!newStorefront.subUrl || newStorefront.subUrl.trim() === '') {
            return false;
        }
        return true;
    }

    return (
        <Dialog
            hidden={!props.isOpen}
            onDismiss={() => {
                if(!saving){
                    props.onClose();
                    setSaving(false); 
                    setErrorMessage('')
                }
            }}
            dialogContentProps={{
                title: 'Copy Storefront',
                showCloseButton: true,
            }}
        >
            <Stack tokens={{childrenGap: '1em'}}>
                {saving ? 
                <div>
                    <ProgressIndicator label="Copying Storefront" description="This may take several minutes. DO NOT NAVIGATE AWAY FROM THIS PAGE" />
                </div> : 
                <React.Fragment>
                    <TextField label='Storefront Sub-URL' value={newStorefront.subUrl} onChange={(_, val) => setNewStorefront({...newStorefront, subUrl: val ?? ''})} required />
                    <TextField label="Storefront Name" value={newStorefront.name} onChange={(_, val) => setNewStorefront({...newStorefront, name: val ?? ''})} required />
                </React.Fragment>}
            </Stack>
            <DialogFooter>
                <Stack horizontal tokens={{childrenGap: '1em'}}>
                    <PrimaryButton disabled={saving || externalDataLoading || !validated()} onClick={save}>{saving ? 'Saving' : 'Copy Storefront'}</PrimaryButton>
                    <DefaultButton disabled={saving} onClick={props.onClose}>Cancel</DefaultButton>
                </Stack>
                {errorMessage && <MessageBar messageBarType={MessageBarType.error}>{errorMessage}</MessageBar>}
            </DialogFooter>
        </Dialog>
    )
}
export default NewStorefrontDialog;
export {CopyStorefrontDialog};